import './NewAdProfile.sass';

import React, { FC, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { adsActions } from '$store/ads';
import { Loader, MetaTags, PromoProfile } from '$shared/components';
import { BottomBar, Button, Header, Placeholder, ScrollView } from '$uikit';
import { useCurrentUserInfo, useProfileInfo, useTranslator } from '$hooks';
import { getImageSizeByUrl, uploadImage } from '$utils';
import { goBack, showModal } from '$navigation/helper';
import { toastActions } from '$store/toast';
import { Api } from '$api';
import { RootState } from '$store/rootReducer';
import { attachFullItemsSelector } from '$store/attach';

export const NewAdProfile: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();

  const user = useCurrentUserInfo();
  const userProfile = useProfileInfo(user.id);
  const adsCover = useSelector((state: RootState) =>
    attachFullItemsSelector(state, 'post_ad_new'),
  );
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [cover, setCover] = useState({
    url: userProfile.coverUrl || '',
    id: userProfile.coverId ?? 0,
  });
  const [avatar, setAvatar] = useState({
    url: user.photo,
    id: user.photoId ?? 0,
  });

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    Api.get<any, any>(`/profile/${user.id}`)
      .then((resp) => {
        setCover({
          url: resp.coverUrl,
          id: resp.coverId ?? 0,
        });
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  }, [user]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (adsCover.length === 0) {
      return;
    }

    const { photo } = adsCover[adsCover.length - 1];

    if (photo === undefined) {
      return;
    }

    setCover({ id: photo.photoId, url: photo.photoMedium });
  }, [adsCover]);

  const handlePhotoChange = useCallback(async ({ target: { files } }) => {
    const fileUrl = URL.createObjectURL(files[0]);
    const { width, height } = await getImageSizeByUrl(fileUrl, false);
    if (height > width * 3) {
      dispatch(toastActions.setToastFail(t('photo_validation_size_err')));
    } else {
      dispatch(toastActions.setToastLoading());
      uploadImage(files[0])
        .then((resp) => {
          setAvatar({
            url: resp.base64,
            id: resp.id,
          });
          dispatch(toastActions.hideToast());
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    }
  }, []);

  const handleContinue = useCallback(() => {
    if (!cover.id) {
      dispatch(toastActions.setToastFail(t('new_ad_profile_no_cover_err')));
      return;
    }

    dispatch(
      adsActions.createPromoProfile({
        cover: cover.id,
        avatar: avatar.id,
      }),
    );
  }, [cover, avatar]);

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    const openCoverModal = () => {
      showModal('cover', false, { isAdCover: true });
    };

    return (
      <>
        <ScrollView>
          <div className="NewAdProfile__title">{t('new_ad_profile_preview')}</div>
          <div className="NewAdProfile__preview">
            <PromoProfile
              cover={cover.url}
              avatar={avatar.url}
              userId={user.id}
              adId={0}
            />
          </div>

          <div className="NewAdProfile__settings">
            <div className="NewAdProfile__settings_title">
              {t('new_ad_profile_settings')}
            </div>
            <Button
              onClick={openCoverModal}
              className="NewAdProfile__settings_button"
              mode="quinary"
            >
              {t('new_ad_profile_upload_cover')}
            </Button>
            <Button className="NewAdProfile__settings_button" mode="quinary">
              {t('new_ad_profile_upload_avatar')}
              <input
                type="file"
                className="NewAdProfile__upload_input"
                accept="image/jpeg, image/jpg, image/png, image/webp"
                onChange={handlePhotoChange}
              />
            </Button>

            <div className="NewAdProfile__settings_caption">
              {t('new_ad_profile_caption')}
            </div>
          </div>
        </ScrollView>

        <BottomBar>
          <Button onClick={handleContinue} stretched>
            {t('new_ad_profile_continue')}
          </Button>
        </BottomBar>
      </>
    );
  }

  return (
    <>
      <MetaTags title={t('new_ad_profile')} />
      <Header showBackButton backButtonOnClick={() => goBack()}>
        {t('new_ad_profile')}
      </Header>
      {renderContent()}
    </>
  );
};
